import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { formatDate } from "../../lib/format.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { ImageWithSkeleton } from "../shared/ImageWithSkeleton.jsx";

export function LatestBlogs({ blogs = [] }) {
  if (blogs.length === 0) return null;

  return (
    <section className="border-t border-border-subtle py-20 lg:py-28">
      <Container>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="eyebrow">Insights</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              From our blog
            </h2>
            <p className="mt-3 text-text-secondary">
              {"Design inspiration, construction tips, and first-home-buyer guides written by the team you'll be building with."}
            </p>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover">
            All posts <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {blogs.slice(0, 3).map((post) => {
            const key = post.slug ?? post._id ?? post.title;
            return (
              <Link
                key={key}
                to={`/blog/${post.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-card-hover"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-bg-elevated-2">
                  <ImageWithSkeleton
                    src={imageOrPlaceholder(post.heroImage, { pool: "blog", key })}
                    onError={imgErrorFallback(key)}
                    alt={post.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {post.category?.name && (
                    <span className="absolute left-4 top-4 z-10 rounded-full bg-bg-base/85 px-3 py-1 text-xs font-medium text-accent backdrop-blur">
                      {post.category.name}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-xl leading-snug text-text-primary group-hover:text-accent">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-2 line-clamp-2 text-sm text-text-secondary">
                      {post.excerpt}
                    </p>
                  )}
                  <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-text-muted">
                    {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
                    {post.readingTime && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {post.readingTime} min read
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
}